import React, { useState, useEffect } from 'react';
import { CreditCardIcon, UsersIcon } from '@heroicons/react/24/outline';
import PaymentForm from '../payment/PaymentForm';
import { License, UserLicense } from '../../types/payment.types';
import { toast } from 'react-hot-toast';
import { useAppSelector } from '../../hooks/useRedux';
import { LicenseService } from '../../services/license.service';
import { Team, TeamService } from '../../services/team.service';
import Modal from './Modal';

interface UpgradePromptModalProps {
    isOpen: boolean;
    onClose: () => void;
    onUpgrade?: () => void;
    teamId: string;
}

interface TeamDetails {
    id: string;
    name: string;
    member_limit: number;
    current_member_count: number;
    license_type: 'trial' | 'paid';
}

const LICENSE_PRICE_PER_USER = 6;

const UpgradePromptModal: React.FC<UpgradePromptModalProps> = ({
    isOpen,
    onClose,
    onUpgrade,
    teamId,
}) => {
    const [showPaymentForm, setShowPaymentForm] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>('');
    const [teamDetails, setTeamDetails] = useState<TeamDetails | null>(null);
    const [additionalLicenses, setAdditionalLicenses] = useState(1);
    const { user } = useAppSelector(state => state.auth);

    useEffect(() => {
        if (isOpen) {
            loadTeamDetails();
        }
    }, [isOpen, teamId]);

    const loadTeamDetails = async () => {
        try {
            setLoading(true);
            setError('');
            
            const team = await TeamService.getTeamById(teamId);
            setTeamDetails(team.team as TeamDetails);
            
            // Set initial additional licenses to 1
            setAdditionalLicenses(1);
        } catch (error) {
            const err = error as Error;
            setError(err.message || 'Failed to load team details');
            console.error('Error loading team:', error);
        } finally {
            setLoading(false);
        }
    };

    const calculatePrice = () => {
        if (!teamDetails) return 0;
        const totalLicenses = teamDetails.license_type === 'trial' 
            ? additionalLicenses + 3 // Include first 3 licenses for trial users
            : additionalLicenses; // Only new licenses for paid users
        return totalLicenses * LICENSE_PRICE_PER_USER;
    };

    const handleUpgradeClick = () => {
        if (calculatePrice() >= 0.50) {
            setShowPaymentForm(true);
        } else {
            setError('Invalid license quantity. Please select at least one license.');
        }
    };

    const handlePaymentSuccess = (paymentIntentId: string) => {
        toast.success('License upgrade successful');
        onClose();
        if (onUpgrade) {
            onUpgrade();
        }
    };

    const handlePaymentError = (error: string) => {
        setError(error);
        toast.error(error);
    };

    const getLicenseUsagePercentage = () => {
        if (!teamDetails) return 0;
        return (teamDetails.current_member_count / teamDetails.member_limit) * 100;
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="License Management">
            <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                {loading ? (
                    <div className="flex justify-center items-center py-2">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                    </div>
                ) : !showPaymentForm ? (
                    <>
                        <div className="space-y-6">
                            {teamDetails && (
                                <div className="bg-white dark:bg-gray-700 rounded-lg p-4 shadow-sm">
                                    <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                                        User License Usage
                                    </h4>
                                    
                                    <div className="space-y-4">
                                        <div>
                                            <div className="flex justify-between text-sm text-gray-600 dark:text-gray-300 mb-1">
                                                <span>Current Usage</span>
                                                <span>{teamDetails.current_member_count} / {teamDetails.member_limit} licenses</span>
                                            </div>
                                            <div className="w-full bg-gray-200 rounded-full h-2.5">
                                                <div 
                                                    className={`h-2.5 rounded-full ${
                                                        getLicenseUsagePercentage() > 90 ? 'bg-red-500' : 
                                                        getLicenseUsagePercentage() > 75 ? 'bg-yellow-500' : 
                                                        'bg-blue-500'
                                                    }`}
                                                    style={{ width: `${Math.min(getLicenseUsagePercentage(), 100)}%` }}
                                                ></div>
                                            </div>
                                        </div>

                                        <div className="flex justify-between items-center text-sm">
                                            <span className="text-gray-600 dark:text-gray-300">Available Licenses:</span>
                                            <span className="font-medium text-gray-900 dark:text-white">
                                                {teamDetails.member_limit - teamDetails.current_member_count}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-4">
                                <h4 className="text-md font-medium text-blue-900 dark:text-blue-100 mb-3">
                                    Add More Licenses
                                </h4>
                                <div className="space-y-4">
                                    <div className="flex items-center space-x-4">
                                        <button
                                            type="button"
                                            onClick={() => setAdditionalLicenses(Math.max(1, additionalLicenses - 1))}
                                            className="p-2 rounded-md bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
                                        >
                                            -
                                        </button>
                                        <input
                                            type="number"
                                            min="1"
                                            value={additionalLicenses}
                                            onChange={(e) => setAdditionalLicenses(Math.max(1, parseInt(e.target.value) || 1))}
                                            className="block w-20 text-center border-gray-300 dark:border-gray-600 rounded-md shadow-sm dark:bg-gray-700"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setAdditionalLicenses(additionalLicenses + 1)}
                                            className="p-2 rounded-md bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
                                        >
                                            +
                                        </button>
                                </div>

                                    <div className="text-sm text-blue-700 dark:text-blue-300 space-y-2">
                                        {teamDetails?.license_type === 'trial' ? (
                                            <>
                                                <p>• Converting from free to paid license</p>
                                                <p>• Includes your first 3 licenses + {additionalLicenses} additional</p>
                                                <p>• Total licenses after upgrade: {3 + additionalLicenses}</p>
                                                <p>• One-time payment of ${calculatePrice()}</p>
                                        </>
                                    ) : (
                                        <>
                                                <p>• Adding {additionalLicenses} new license{additionalLicenses > 1 ? 's' : ''}</p>
                                                <p>• New total limit will be: {(teamDetails?.member_limit || 0) + additionalLicenses}</p>
                                                <p>• One-time payment of ${calculatePrice()}</p>
                                        </>
                                    )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                            <button
                                type="button"
                                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                                onClick={handleUpgradeClick}
                                disabled={!!error}
                            >
                                {teamDetails?.license_type === 'trial' 
                                    ? `Upgrade Now ($${calculatePrice()})`
                                    : `Add Licenses ($${calculatePrice()})`}
                            </button>
                            <button
                                type="button"
                                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm"
                                onClick={onClose}
                            >
                                Cancel
                            </button>
                        </div>
                    </>
                ) : (
                    <PaymentForm
                        teamId={teamId}
                        price={calculatePrice()}
                        memberLimit={teamDetails?.license_type === 'trial' ? additionalLicenses + 3 : additionalLicenses}
                        licenseType="standard"
                        onSuccess={handlePaymentSuccess}
                        onError={handlePaymentError}
                    />
                )}
                {error && (
                    <div className="mt-4 p-3 bg-red-50 text-red-700 rounded-md">
                        {error}
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default UpgradePromptModal; 